import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const simulatedCursorClickSkill: Skill = {
    name: SKILL_NAMES.simulatedCursorClick,
    kind: 'codeo',
    description:
        'Drive a stylized cursor across a mocked interface and click something, with the interface ' +
        'responding — for showing how a product is used in two seconds, without a screen recording.',
    instructions: `# Simulated Cursor Click

An oversized cursor arcs across a simplified interface, presses, and the interface responds. It is the fastest way to show *use* — a real screen recording is too small to read and too slow to cut, whereas a staged click lands the interaction in about a second.

The interface must be a simplification, not a replica. Three or four large elements, one of which is the target. A faithful reproduction of a real UI at video scale is unreadable and reads as a screenshot.

## Markup

Make the cursor much larger than a real one — around 64px — or it disappears at video scale.

\`\`\`html
<div id="ui" style="position: absolute; left: 360px; top: 260px; width: 1200px; height: 560px; border-radius: 24px; overflow: hidden; background: var(--l5-color-1, #ffffff); box-shadow: 0 40px 90px rgba(0,0,0,0.28);">
  <div id="ui-target" style="position: absolute; left: 120px; top: 380px; width: 340px; height: 88px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 34px; font-weight: 700; color: var(--l5-color-1, #ffffff);">Publish</div>
  <div id="ui-result" style="position: absolute; left: 520px; top: 380px; font-family: var(--l5-font-0), sans-serif; font-size: 34px; font-weight: 600; color: var(--l5-color-0, #101820);">Live in 4 seconds</div>
</div>
<svg id="cursor" viewBox="0 0 24 24" style="position: absolute; left: 0; top: 0; width: 64px; height: 64px; filter: drop-shadow(0 6px 14px rgba(0,0,0,0.35));">
  <path d="M5 2 L5 20 L10 15.5 L13 22 L16 20.5 L13 14.5 L19.5 14 Z" fill="var(--l5-color-0, #101820)" stroke="var(--l5-color-1, #ffffff)" stroke-width="1.2" />
</svg>
\`\`\`

## Timeline code

The cursor travels on a curve and eases in — a straight linear glide is the tell that this is animation rather than a hand.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const CLICK = 1.35;

gsap.set('#cursor', { x: 1420, y: 880, opacity: 0 });
gsap.set('#ui-result', { opacity: 0, x: -10 });

tl.to('#cursor', { opacity: 1, duration: 0.2 }, 0.3);
tl.to('#cursor', {
    x: 640,
    y: 690,
    duration: 0.85,
    ease: 'power2.inOut',
}, 0.4);

// The press: cursor dips, target compresses, then both release.
tl.to('#cursor', { scale: 0.86, duration: 0.09, ease: 'power2.in' }, CLICK);
tl.to('#ui-target', { scale: 0.96, duration: 0.09, ease: 'power2.in' }, CLICK + 0.02);
tl.to('#cursor', { scale: 1, duration: 0.16, ease: 'power2.out' }, CLICK + 0.09);
tl.to('#ui-target', { scale: 1, duration: 0.22, ease: 'back.out(2)' }, CLICK + 0.11);
tl.to('#ui-result', { opacity: 1, x: 0, duration: 0.4, ease: 'power3.out' }, CLICK + 0.2);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Arc**: passing an intermediate point (two chained tweens with different eases) curves the path. A curved approach reads far more human than a straight one.
- **Touch instead of pointer**: replace the cursor with a translucent circle that scales down on contact and leaves a single expanding ring. Same choreography, mobile register.
- **Overshoot and correct**: the cursor arriving slightly past the target and settling back reads as a real hand. Very small — 12px.
- **Type instead of click**: land the cursor in a field and hand off to \`${SKILL_NAMES.typewriterWithCaret}\`.
- **Two clicks**: possible but expensive in time. If the point needs two interactions, it is probably two scenes.
- **Result placement**: the response must appear where the eye already is — next to the target, not across the frame.

## Pitfalls

- Cursor and target are \`scale\`- and \`x\`/\`y\`-tweened, so neither may carry a CSS \`transform\`; the cursor's resting position lives entirely in the tween.
- Never draw a real cursor at real size. 56–72px is the readable range at 1920 wide.
- The response must be immediate — within about 0.2s of the press. Any later and the click appears not to have worked.
- Don't animate the whole interface in first and then click. The UI should be settled before the cursor enters, or there are two things moving and neither reads.
- Keep the interface to a handful of elements. This is a diagram of an interaction, not a screenshot.
- Don't show a cursor in a graphic that is not about using software — the style guidance rules out cursors and UI furniture everywhere else.
- With narration, the press lands on the verb ("just **click** publish"), not near it.`,
};
